import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from './client'

export interface MyOrg {
  /** Needed by any org-scoped endpoint — those take the UUID, not the slug. */
  id: string
  name: string
  slug: string
  type: string
  logoUrl?: string
  brandColor?: string
  role: string
  isTeamCaptain: boolean
}

interface ApiOrgItem {
  id: string
  name: string
  slug: string
  type: string
  logo_url: string
  primary_color: string
  role: string
  joined_at: string
  is_team_captain: boolean
}

interface ApiResponse {
  organizations: ApiOrgItem[]
  count: number
  max: number
}

// Simple event target so any code can trigger a refetch of the org list.
const myOrgsEvents = new EventTarget()

/** Call this after creating/joining/leaving an org to update all useMyOrgs consumers. */
export function refreshMyOrgs() {
  myOrgsEvents.dispatchEvent(new Event('refresh'))
}

/**
 * Hook that fetches the current user's organizations.
 * Returns empty array if not authenticated or on error.
 * Call refreshMyOrgs() anywhere to trigger a refetch.
 */
export function useMyOrgs(isAuthenticated: boolean): MyOrg[] {
  const [orgs, setOrgs] = useState<MyOrg[]>([])
  const [version, setVersion] = useState(0)

  // Listen for refresh events
  useEffect(() => {
    const handler = () => setVersion((v) => v + 1)
    myOrgsEvents.addEventListener('refresh', handler)
    return () => myOrgsEvents.removeEventListener('refresh', handler)
  }, [])

  const fetchOrgs = useCallback(() => {
    if (!isAuthenticated) {
      setOrgs([])
      return
    }

    let cancelled = false
    apiFetch<ApiResponse>('/organizations/mine')
      .then((data) => {
        if (cancelled) return
        setOrgs(
          data.organizations.map((o) => ({
            id: o.id,
            name: o.name,
            slug: o.slug,
            type: o.type,
            logoUrl: o.logo_url || undefined,
            brandColor: o.primary_color || undefined,
            role: o.role,
            isTeamCaptain: o.is_team_captain ?? false,
          })),
        )
      })
      .catch(() => {
        if (!cancelled) setOrgs([])
      })

    return () => { cancelled = true }
  }, [isAuthenticated])

  useEffect(() => fetchOrgs(), [fetchOrgs, version])

  return orgs
}
